import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, IconButton, Tooltip, Divider, Typography, Menu, MenuItem,
  ListItemIcon, ListItemText, Fade
} from '@mui/material';
import {
  Scale as ScaleIcon,
  Room as RoomIcon,
  CalendarMonth as CalendarMonthIcon,
  MedicalInformation as MedicalInformationIcon,
  ReceiptLong as ReceiptLongIcon,
  FolderOpen as FolderOpenIcon,
  EventNote as EventNoteIcon,
  Delete as DeleteIcon,
  PetsOutlined as PetsOutlinedIcon,
} from '@mui/icons-material';

const PacienteAccionesBar = ({
  paciente,
  categories = [],
  onControlPeso,
  onAsignarRuta,
  onVerCitasCalendario,
  onRegistrarConsulta,
  onAdministrarRecetas,
  onAdministrarCitas,
  onExpedienteDigital,
  onEliminar,
  onClearSelection,
}) => {
  const [citasAnchorEl, setCitasAnchorEl] = useState(null);
  const disabled = !paciente;
  const pacienteId = paciente?.id_paciente ?? null;

  const closeCitasMenu = () => setCitasAnchorEl(null);

  // 1. Definición de acciones incluyendo el atajo de teclado
  const acciones = [
    {
      key: 'peso',
      shortcut: 'F4',
      label: 'Control de Peso',
      icon: <ScaleIcon fontSize="small" />,
      onClick: () => onControlPeso?.(pacienteId),
    },
    {
      key: 'ruta',
      shortcut: 'F6',
      label: 'Asignar Ruta',
      icon: <RoomIcon fontSize="small" />,
      onClick: () => onAsignarRuta?.(paciente),
      disabled: !paciente?.qr_code,
      tooltip: !paciente?.qr_code ? 'Sin código QR' : 'Asignar Ruta (F6)',
    },
    {
      key: 'calendario',
      shortcut: 'F7',
      label: 'Ver Citas',
      icon: <CalendarMonthIcon fontSize="small" />,
      onClick: () => onVerCitasCalendario?.(pacienteId),
    },
    {
      key: 'consulta',
      shortcut: 'F8',
      label: 'Consulta',
      icon: <MedicalInformationIcon fontSize="small" />,
      onClick: () => onRegistrarConsulta?.(pacienteId),
    },
    {
      key: 'recetas',
      shortcut: 'F9',
      label: 'Recetas',
      icon: <ReceiptLongIcon fontSize="small" />,
      onClick: () => onAdministrarRecetas?.(pacienteId),
    },
    {
      key: 'expediente',
      shortcut: 'F10',
      label: 'Expediente Digital',
      icon: <FolderOpenIcon fontSize="small" />,
      onClick: () => onExpedienteDigital?.(paciente),
    },
  ];

  // 2. Listener centralizado para capturar las teclas asignadas
  const handleKeyDown = useCallback((e) => {
    // Si no hay paciente seleccionado o se está escribiendo en un input, ignorar
    if (disabled) return;
    const isEditing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
    if (isEditing) return;

    // Buscar si la tecla presionada coincide con alguna acción habilitada
    const accionEncontrada = acciones.find(
      (acc) => acc.shortcut && acc.shortcut.toUpperCase() === e.key.toUpperCase() && !acc.disabled
    );

    if (accionEncontrada) {
      e.preventDefault();
      accionEncontrada.onClick();
    }
  }, [disabled, paciente, acciones]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (disabled) return null;

  return (
    <Fade in={!disabled}>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          left: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          py: 1.5,
          px: 1,
          borderRadius: 30,
          bgcolor: 'rgba(255, 255, 255, 0.88)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <Tooltip title={paciente?.nombre_paciente || 'Paciente'} placement="right">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 0.5 }}>
            <PetsOutlinedIcon color="primary" fontSize="small" />
            <Typography
              variant="caption"
              sx={{
                mt: 0.3,
                fontWeight: 600,
                maxWidth: 70,
                textAlign: 'center',
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {paciente?.nombre_paciente?.split(' ')[0] || 'Paciente'}
            </Typography>
          </Box>
        </Tooltip>

        <Divider sx={{ width: '70%', my: 0.5 }} />

        {/* Renderizado con el atajo incluido en el Tooltip */}
        {acciones.map(({ key, label, shortcut, icon, onClick, disabled: isDisabled, tooltip }) => {
          const titleText = tooltip || (shortcut ? `${label} (${shortcut})` : label);

          return (
            <Tooltip key={key} title={titleText} placement="right" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={isDisabled}
                  onClick={onClick}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {icon}
                </IconButton>
              </span>
            </Tooltip>
          );
        })}

        <Tooltip title="Administrar Citas" placement="right" arrow>
          <IconButton
            size="small"
            onClick={(e) => setCitasAnchorEl(e.currentTarget)}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
            }}
          >
            <EventNoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={citasAnchorEl}
          open={Boolean(citasAnchorEl)}
          onClose={closeCitasMenu}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        >
          {categories.map((cat) => (
            <MenuItem
              key={cat.id}
              onClick={() => {
                onAdministrarCitas?.(pacienteId, cat.id);
                closeCitasMenu();
              }}
            >
              <ListItemIcon>{cat.icon}</ListItemIcon>
              <ListItemText>{cat.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        <Divider sx={{ width: '70%', my: 0.5 }} />

        <Tooltip title="Deshabilitar paciente" placement="right">
          <IconButton
            size="small"
            color="error"
            onClick={() => onEliminar?.(pacienteId)}
            sx={{ border: '1px solid', borderColor: 'error.light' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>
    </Fade>
  );
};

export default PacienteAccionesBar;